import { useEffect, useMemo, useState } from "react";
import { PanelStore } from "./types";

interface UseStoreSelectionDraftParams {
  /** Seleção já aplicada (fonte de verdade externa). */
  committed: number[];
  /** Lojas visíveis no filtro atual (base do "selecionar/desmarcar todas"). */
  filteredStores: PanelStore[];
  /** Quando o painel abre, o rascunho é re-sincronizado com `committed`. */
  isOpen: boolean;
}

/**
 * Gerencia a seleção em rascunho do painel: o usuário marca/desmarca lojas e só
 * confirma no "Aplicar". Ao abrir, o rascunho espelha a seleção aplicada.
 */
export function useStoreSelectionDraft({
  committed,
  filteredStores,
  isOpen,
}: UseStoreSelectionDraftParams) {
  const [staged, setStaged] = useState<number[]>(committed);

  useEffect(() => {
    if (isOpen) setStaged(committed);
  }, [isOpen, committed]);

  const allFilteredStaged = useMemo(
    () =>
      filteredStores.length > 0 &&
      filteredStores.every(store => staged.includes(store.code)),
    [filteredStores, staged],
  );

  const toggle = (code: number) => {
    setStaged(prev =>
      prev.includes(code) ? prev.filter(item => item !== code) : [...prev, code],
    );
  };

  const toggleAllFiltered = () => {
    const filteredCodes = filteredStores.map(store => store.code);
    setStaged(prev => {
      if (allFilteredStaged) {
        const toRemove = new Set(filteredCodes);
        return prev.filter(code => !toRemove.has(code));
      }
      return Array.from(new Set([...prev, ...filteredCodes]));
    });
  };

  const reset = () => setStaged(committed);
  const clear = () => setStaged([]);

  return { staged, allFilteredStaged, toggle, toggleAllFiltered, reset, clear };
}
